import type { TFunction } from "i18next";
import {
  ConversationPriority,
  ConversationStatus,
  TeamScope,
  TicketQuery,
  type UpdateConversationMutation,
} from "@lytra/frontend-common/__generated__/graphql";
import { CONVERSATION_PRIORITY_NUMBER_MAP } from "@/consts/conversation";

/** True when a human agent still has to act on the ticket (New or Open); used by the list rows and the strip for the left indicator bar. */
export function needsAgentAction(status: ConversationStatus): boolean {
  return (
    status === ConversationStatus.New || status === ConversationStatus.Open
  );
}

/** True for terminal states (Closed; Solved only remains on legacy rows); rows and strip grey out such tickets. */
export function isClosedStatus(status: ConversationStatus): boolean {
  return (
    status === ConversationStatus.Closed || status === ConversationStatus.Solved
  );
}

// The built-in categories in board order. New was merged into Open and Solved is legacy:
// neither is offered.
export const STATUS_ORDER: ConversationStatus[] = [
  ConversationStatus.Open,
  ConversationStatus.Pending,
  ConversationStatus.OnHold,
  ConversationStatus.Closed,
];

export const PRIORITY_ORDER: ConversationPriority[] = [
  ConversationPriority.Urgent,
  ConversationPriority.High,
  ConversationPriority.Medium,
  ConversationPriority.Low,
];

export const statusLabel = (t: TFunction, status: ConversationStatus): string =>
  t(`conversationStatus.${status}`);

export const priorityName = (
  t: TFunction,
  priority: ConversationPriority,
): string => t(`conversationPriority.${priority}`);

export const priorityOptionLabel = (
  t: TFunction,
  priority: ConversationPriority,
): string =>
  t("tickets.filters.priorityOption", {
    number: CONVERSATION_PRIORITY_NUMBER_MAP[priority],
    label: priorityName(t, priority),
  });

export const personName = (person?: {
  givenName?: string | null;
  familyName?: string | null;
}): string | null => {
  const parts = [person?.givenName, person?.familyName].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : null;
};

/** The list with the current user's entry moved to the front, the rest in order. */
export function agentsSelfFirst<T extends { id: string }>(
  agents: readonly T[],
  currentUserId: string | undefined,
): T[] {
  const own = agents.find((agent) => agent.id === currentUserId);
  if (!own) return [...agents];
  return [own, ...agents.filter((agent) => agent !== own)];
}

// Radix Select rejects an empty item value, so "no assignee" travels as this
// sentinel and is turned back into a null assignee on select.
export const UNASSIGNED_VALUE = "__unassigned__";

/** One row of the assignee picker: a team, a person under a team, or a person in no team. */
export interface AssigneeOption {
  value: string;
  label: string;
  team?: boolean;
  nested?: boolean;
  /** The owner filter's "team only" row: narrows to the team itself, no members. Styled
   * down from a member row -- it is the rare choice, kept legible only by sitting right
   * after that team's members. */
  teamOnly?: boolean;
}

/** The assignee picker (#960): a ticket belongs to a person or to a team. Each team is
 * listed with its members below it and the people in no team follow; without teams it
 * is the plain list of people. A person in several teams is listed under each, so a
 * person's value also names the team row, which only keeps the values unique. People
 * without a name (provisioned accounts that never signed in) are left out. */
export function assigneeOptions(
  agents: readonly {
    id: string;
    givenName?: string | null;
    familyName?: string | null;
  }[],
  teams: readonly {
    id: string;
    name: string;
    members: readonly { id: string }[];
  }[],
  currentUserId: string | undefined,
  unassignedLabel: string,
): AssigneeOption[] {
  const people = agentsSelfFirst(agents, currentUserId).flatMap((agent) => {
    const label = personName(agent);
    return label ? [{ id: agent.id, label }] : [];
  });
  const inATeam = new Set(
    teams.flatMap((team) => team.members.map((m) => m.id)),
  );
  return [
    { value: UNASSIGNED_VALUE, label: unassignedLabel },
    ...teams.flatMap((team) => {
      const members = new Set(team.members.map((m) => m.id));
      return [
        { value: `team:${team.id}`, label: team.name, team: true },
        ...people
          .filter((person) => members.has(person.id))
          .map((person) => ({
            value: `agent:${person.id}:${team.id}`,
            label: person.label,
            nested: true,
          })),
      ];
    }),
    ...people
      .filter((person) => !inATeam.has(person.id))
      .map((person) => ({ value: `agent:${person.id}`, label: person.label })),
  ];
}

/** What a picked assignee value assigns; the other side is released. */
export function parseAssigneeValue(value: string): {
  agentId: string | null;
  teamId: string | null;
} {
  const [kind, id] = value.split(":");
  if (kind === "team") return { agentId: null, teamId: id };
  if (kind === "agent") return { agentId: id, teamId: null };
  return { agentId: null, teamId: null };
}

/** The picker row showing the ticket's assignee: the team, or the person's first row. */
export function assigneeValue(
  options: readonly AssigneeOption[],
  assignee: { agentId?: string | null; teamId?: string | null },
): string {
  const match = options.find((option) => {
    const picked = parseAssigneeValue(option.value);
    return assignee.teamId
      ? picked.teamId === assignee.teamId
      : !!assignee.agentId && picked.agentId === assignee.agentId;
  });
  return match?.value ?? UNASSIGNED_VALUE;
}

/** The ticket header's "Mir zuweisen" shortcut: the picker row that assigns the ticket to the
 * viewer, the same value picking oneself in the menu sends. Null when the shortcut has nothing
 * to do: the ticket is already the viewer's own as a person (a ticket of the viewer's team can
 * still be taken), or the viewer is not in the picker (no name, not loaded yet). */
export function assignToMeValue(
  options: readonly AssigneeOption[],
  currentValue: string | undefined,
  currentUserId: string | undefined,
): string | null {
  if (!currentUserId) return null;
  if (
    currentValue &&
    parseAssigneeValue(currentValue).agentId === currentUserId
  )
    return null;
  const own = options.find(
    (option) => parseAssigneeValue(option.value).agentId === currentUserId,
  );
  return own?.value ?? null;
}

// The owner filter's row for a team's own tickets, without those of its members.
export const TEAM_ONLY_PREFIX = "team-only:";

/** The ticket list's owner filter (#960): the header picker's rows without "Nicht
 * zugewiesen", which the filter offers on its own next to "Alle Zuständigen". A team row
 * is itself the broad choice -- the team's tickets and its members' -- so selecting it
 * needs no separate "with members" row. Its narrower sibling ("Nur dem Team direkt
 * zugewiesen") is appended after that team's members, not before them: it reads
 * against the team's own name and member list right above it, not against whichever
 * row happened to precede it. */
export function assigneeFilterOptions(
  agents: Parameters<typeof assigneeOptions>[0],
  teams: Parameters<typeof assigneeOptions>[1],
  currentUserId: string | undefined,
  labels: { teamOnly: string },
): AssigneeOption[] {
  const base = assigneeOptions(agents, teams, currentUserId, "").filter(
    (option) => option.value !== UNASSIGNED_VALUE,
  );
  const result: AssigneeOption[] = [];
  let openTeamId: string | null = null;
  const closeTeam = () => {
    if (!openTeamId) return;
    result.push({
      value: `${TEAM_ONLY_PREFIX}${openTeamId}`,
      label: labels.teamOnly,
      nested: true,
      teamOnly: true,
    });
    openTeamId = null;
  };
  for (const option of base) {
    const belongsToOpenTeam =
      openTeamId !== null &&
      option.nested &&
      option.value.endsWith(`:${openTeamId}`);
    if (!belongsToOpenTeam) closeTeam();
    result.push(option);
    if (option.team) openTeamId = parseAssigneeValue(option.value).teamId;
  }
  closeTeam();
  return result;
}

/** The ticket list's owner filter: a person, a team, or neither (both null). `teamScope`
 * says whether a team filter also matches its members' tickets. */
export interface ConversationOwnerFilter {
  assigneeId: string | null;
  teamId: string | null;
  teamScope: TeamScope;
  /** "Nicht zugewiesen": only tickets with neither a person nor a team. */
  unassigned: boolean;
}

/** No owner filter. A team picked later matches its members too unless "nur Team". */
export const NO_OWNER_FILTER: ConversationOwnerFilter = {
  assigneeId: null,
  teamId: null,
  teamScope: TeamScope.TeamAndMembers,
  unassigned: false,
};

/** What a picked owner filter row filters to. */
export function parseOwnerFilterValue(value: string): ConversationOwnerFilter {
  if (value === UNASSIGNED_VALUE)
    return { ...NO_OWNER_FILTER, unassigned: true };
  if (value.startsWith(TEAM_ONLY_PREFIX))
    return {
      ...NO_OWNER_FILTER,
      teamId: value.slice(TEAM_ONLY_PREFIX.length),
      teamScope: TeamScope.TeamOnly,
    };
  const picked = parseAssigneeValue(value);
  return {
    ...NO_OWNER_FILTER,
    assigneeId: picked.agentId,
    teamId: picked.teamId,
  };
}

/** A stored team scope; a filter stored before the scope existed matched the team only. */
export const asTeamScope = (value: unknown): TeamScope =>
  value === TeamScope.TeamAndMembers || value === TeamScope.TeamOnly
    ? value
    : TeamScope.TeamOnly;

/** The picker row that shows the list's owner filter, or "" for the placeholder: when no
 * owner is filtered, when "Meine Tickets" is on (its own switch), or when the filtered
 * person or team is no longer listed. */
export function assigneeFilterValue(
  options: readonly AssigneeOption[],
  filter: ConversationOwnerFilter,
  currentUserId: string | undefined,
): string {
  if (filter.unassigned) return UNASSIGNED_VALUE;
  if (
    !filter.teamId &&
    (!filter.assigneeId || filter.assigneeId === currentUserId)
  )
    return "";
  if (filter.teamId && filter.teamScope === TeamScope.TeamOnly) {
    const value = `${TEAM_ONLY_PREFIX}${filter.teamId}`;
    return options.some((option) => option.value === value) ? value : "";
  }
  const value = assigneeValue(options, {
    agentId: filter.assigneeId,
    teamId: filter.teamId,
  });
  return value === UNASSIGNED_VALUE ? "" : value;
}

export const isTeamValue = (value?: string): boolean =>
  !!value?.startsWith("team:");

/** "Name — Company" when both exist, else name, company, email, or the localized "unknown". */
export function contactLine(
  t: TFunction,
  contact:
    | {
        givenName?: string | null;
        familyName?: string | null;
        emailAddress?: string | null;
      }
    | null
    | undefined,
  companyName?: string | null,
): string {
  const name = personName(contact ?? undefined);
  if (name && companyName) return `${name} — ${companyName}`;
  return (
    name ?? companyName ?? contact?.emailAddress ?? t("tickets.meta.unknown")
  );
}

export function formatEmailContact(
  contact:
    | {
        givenName?: string | null;
        familyName?: string | null;
        name?: string | null;
        emailAddress?: string | null;
      }
    | null
    | undefined,
): string {
  if (!contact) return "";

  const nameParts = contact.name ?? personName(contact) ?? "";

  const emailPart = contact.emailAddress ? `<${contact.emailAddress}>` : "";

  return `${nameParts} ${emailPart}`.trim();
}

/**
 * Narrow an untyped select value (the header select hands back a plain string)
 * to a member of a generated GraphQL enum.
 */
export function isEnumMember<T extends string>(
  enumObject: Record<string, T>,
  value: string,
): value is T {
  return Object.values<string>(enumObject).includes(value);
}

export function convertPlaintextToTiptapHTML(plainText: string) {
  if (!plainText?.trim()) {
    return '<p class="text-node"></p>';
  }

  // split by double newlines to get paragraphs
  const paragraphs = plainText.split("\n\n").map((paragraph) => {
    // handle single newlines as line breaks
    const lines = paragraph.split("\n").map((line) => line.trim());

    // join lines with <br> tags
    const paragraphContent = lines.join("<br>");

    // wrap in paragraph tag
    return `<p class="text-node">${paragraphContent}</p>`;
  });

  return paragraphs.join("");
}

/**
 * An image URL in square brackets on its own, which is how a plain-text part
 * shows an `<img>` that has no link around it: `[https://host/a.png]`.
 */
export const BARE_IMAGE_LINK_REGEX =
  /\[(https?:\/\/[^\s\]<>"]+\.(?:png|jpe?g|gif|webp|svg|avif)(?:\?[^\s\]<>"]*)?)\](?!<)/gi;

/** Splits trailing sentence punctuation and unbalanced closers off a matched URL. */
const splitTrailingPunctuation = (url: string): [string, string] => {
  let end = url.length;
  while (end > 0) {
    const last = url[end - 1];
    if (last && ".,;:!?]'".includes(last)) {
      end--;
    } else if (last === ")" && !url.slice(0, end).includes("(")) {
      end--;
    } else {
      break;
    }
  }
  return [url.slice(0, end), url.slice(end)];
};

export function processContent(
  content: string,
  isHtml: boolean,
  loadRemoteImages: boolean,
): string {
  const safeLinksRegex =
    /https:\/\/[a-zA-Z0-9-]+\.safelinks\.protection\.outlook\.com\/\?url=([^&]+)(?:&[^"'\s<>]+)/g;
  const urlRegex = /(https?:\/\/[^\s<>"]+)/g;
  const msGraphImageLinkRegex = /\[(http[^\]]+)\]<(http[^>]+)>/g;
  const msGraphSimpleLinkRegex = /<(http[^>]+)>/g;
  const msGraphTextLinkRegex = /\[([^\]]+)\]<(https?:\/\/[^>]+)>/g;

  if (isHtml) {
    // Create a DOM parser and parse the HTML content
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");

    // Process text nodes to handle SafeLinks
    const processTextNode = (node: Text) => {
      const newContent = node.textContent?.replace(
        safeLinksRegex,
        (match, encodedUrl: string) => {
          try {
            const decodedUrl = decodeURIComponent(encodedUrl);
            return `<a href="${match}" target="_blank" rel="noopener noreferrer">${decodedUrl}</a>`;
          } catch {
            return match;
          }
        },
      );
      if (newContent !== node.textContent) {
        const span = doc.createElement("span");
        span.innerHTML = newContent ?? "";
        node.parentNode?.replaceChild(span, node);
      }
    };

    // Process anchor tags to handle SafeLinks in their text content
    const processAnchorTag = (anchor: HTMLAnchorElement) => {
      if (anchor.textContent?.includes("safelinks.protection.outlook.com")) {
        const match = anchor.textContent.match(safeLinksRegex);
        if (match) {
          try {
            const urlMatch = /url=([^&]+)/.exec(match[0]);
            if (!urlMatch?.[1]) return;
            const decodedUrl = decodeURIComponent(urlMatch[1]);
            anchor.textContent = decodedUrl;
          } catch {
            // Keep original text if decoding fails
          }
        }
      }
    };

    // Walk through all nodes in the document
    const walker = doc.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      null,
    );

    let node: Node | null;
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE) {
        processTextNode(node as Text);
      } else if (
        node.nodeType === Node.ELEMENT_NODE &&
        (node as Element).tagName === "A"
      ) {
        processAnchorTag(node as HTMLAnchorElement);
      }
    }

    // Only modify image handling if not loading remote images
    if (!loadRemoteImages) {
      // Find all images that aren't data URLs
      const images = doc.querySelectorAll("img");
      images.forEach((img) => {
        const src = img.getAttribute("src");
        if (src && !src.startsWith("data:")) {
          img.removeAttribute("src");
        }
      });

      content = doc.body.innerHTML;
    }

    return content;
  } else {
    // Store link replacements with placeholders
    const linkReplacements: { placeholder: string; replacement: string }[] = [];
    let linkIndex = 0;
    let processedContent = content;

    // Helper function to add a link replacement
    const addLinkReplacement = (replacement: string): string => {
      const placeholder = `__LINK${linkIndex}__`;
      linkReplacements.push({ placeholder, replacement });
      linkIndex++;
      return placeholder;
    };

    // 1. Handle MS Graph text links first
    processedContent = processedContent.replace(
      msGraphTextLinkRegex,
      (match, text: string, url: string) => {
        // Only process if the text part is not a URL
        if (!text.match(urlRegex)) {
          return addLinkReplacement(
            `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`,
          );
        }
        return match; // Return unchanged if text is a URL
      },
    );

    // 2. Handle MS Graph image links first
    processedContent = processedContent.replace(
      msGraphImageLinkRegex,
      (_match, imageUrl: string, clickableUrl: string) => {
        const imgTag = loadRemoteImages
          ? `<img src="${imageUrl}" />`
          : '<span class="text-muted-foreground">[Bild blockiert]</span>';
        return addLinkReplacement(
          `<a href="${clickableUrl}" target="_blank" rel="noopener noreferrer">${imgTag}</a>`,
        );
      },
    );

    // 3. Handle MS Graph simple links
    processedContent = processedContent.replace(
      msGraphSimpleLinkRegex,
      (match, url: string, offset: number) => {
        // Check if this link is at the start of a line
        const textBeforeMatch = processedContent.substring(0, offset);
        const isStartOfLine =
          !textBeforeMatch || textBeforeMatch.endsWith("\n");
        const spaceBefore = isStartOfLine ? "" : " ";

        if (url.includes("safelinks.protection.outlook.com")) {
          const safeLinksMatch = url.match(safeLinksRegex);
          if (safeLinksMatch) {
            try {
              const urlMatch = /url=([^&]+)/.exec(safeLinksMatch[0]);
              if (!urlMatch?.[1]) return match;
              const decodedUrl = decodeURIComponent(urlMatch[1]);
              return addLinkReplacement(
                `${spaceBefore}<a href="${url}" target="_blank" rel="noopener noreferrer">${decodedUrl}</a>`,
              );
            } catch {
              return match;
            }
          }
        }
        return addLinkReplacement(
          `${spaceBefore}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
        );
      },
    );

    // 4. Handle remaining SafeLinks
    processedContent = processedContent.replace(
      safeLinksRegex,
      (fullMatch: string, encodedUrl: string) => {
        try {
          const decodedUrl = decodeURIComponent(encodedUrl);
          return addLinkReplacement(
            `<a href="${fullMatch}" target="_blank" rel="noopener noreferrer">${decodedUrl}</a>`,
          );
        } catch {
          return addLinkReplacement(
            `<a href="${fullMatch}" target="_blank" rel="noopener noreferrer">${fullMatch}</a>`,
          );
        }
      },
    );

    // 5. Bare `[image-url]`: show the image inline, linked to the original
    processedContent = processedContent.replace(
      BARE_IMAGE_LINK_REGEX,
      (_match, imageUrl: string) => {
        const imgTag = loadRemoteImages
          ? `<img src="${imageUrl}" alt="" class="max-w-full h-auto rounded-md" />`
          : '<span class="text-muted-foreground">[Bild blockiert]</span>';
        return addLinkReplacement(
          `<a href="${imageUrl}" target="_blank" rel="noopener noreferrer">${imgTag}</a>`,
        );
      },
    );

    // 6. Handle remaining regular URLs that aren't already in anchor tags
    processedContent = processedContent.replace(urlRegex, (match: string) => {
      // Skip if URL is a SafeLink (already processed via placeholder)
      if (match.includes("safelinks.protection.outlook.com")) {
        return match;
      }
      const [url, trailing] = splitTrailingPunctuation(match);
      return (
        addLinkReplacement(
          `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
        ) + trailing
      );
    });

    // Finally, replace all placeholders with their actual link HTML
    linkReplacements.forEach(({ placeholder, replacement }) => {
      processedContent = processedContent.replace(placeholder, replacement);
    });

    return processedContent;
  }
}

export interface MessageContentData {
  text: string | null;
  html: string | null;
  emailFullText: string | null;
  emailFullHtml: string | null;
  emailSubject: string | null;
  emailForwardedSubject: string | null;
  emailForwardedContentText: string | null;
  languageCode: string | null;
}

export function getMessageContent(
  message: NonNullable<
    TicketQuery["conversationOne"]
  >["messages"]["edges"][number]["node"],
): MessageContentData {
  return {
    text: message.contentText ?? null,
    html: message.contentHtml ?? null,
    emailFullText: message.emailFullContentText ?? null,
    emailFullHtml: message.emailFullContentHtml ?? null,
    emailSubject: message.emailSubject ?? null,
    emailForwardedSubject: message.emailForwardedSubject ?? null,
    emailForwardedContentText: message.emailForwardedContentText ?? null,
    languageCode: message.languageCode ?? null,
  };
}

export function formatFileSize(bytes: number): string {
  return bytes < 1024
    ? `${bytes}B`
    : bytes < 1024 * 1024
      ? `${Math.round(bytes / 1024)}KB`
      : `${Math.round(bytes / (1024 * 1024))}MB`;
}

// Reply/forward markers in the languages our tenants write in, with an
// optional counter such as "Re[2]:" or "AW:".
const SUBJECT_PREFIX =
  /^\s*(?:(?:re|aw|antw|fw|fwd|wg|sv|vs|tr)(?:\[\d+\])?\s*:\s*)+/i;

/** A subject without reply/forward prefixes, whitespace collapsed, lower-cased. */
export function normalizeSubject(subject: string | null | undefined): string {
  return (subject ?? "")
    .replace(SUBJECT_PREFIX, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * True when `subject` says something other than the thread's first subject
 * once "Re:"/"Aw:"/"Fwd:" prefixes are ignored. An empty subject never differs.
 */
export function subjectDiverges(
  subject: string | null | undefined,
  threadSubject: string | null | undefined,
): boolean {
  const normalized = normalizeSubject(subject);
  return normalized !== "" && normalized !== normalizeSubject(threadSubject);
}

/** What a header change sends to `conversationUpdate`: a field left out stays as it is. */
export interface ConversationChange {
  status?: ConversationStatus;
  /** The ticket's status (sent with its category, ADR-0078); null picks the category's
   * default. */
  ticketStatusId?: string | null;
  priority?: ConversationPriority;
  /** An assignee change always sets both ids (parseAssigneeValue); the other side is released. */
  agentId?: string | null;
  teamId?: string | null;
}

interface UpdatedAgent {
  id: string;
  givenName?: string | null;
  familyName?: string | null;
}

interface UpdatedTeam {
  id: string;
  name: string;
}

/**
 * The optimistic response of a header change (status, priority, assignee): the chips, the header
 * and the list show the new value at once, and Apollo rolls it back if the mutation fails. The
 * server applies exactly the ids sent, so the predicted owner is the picked person or team (null
 * for both: unassigned); without an assignee change the current owner stays.
 */
export function conversationOptimisticResponse(
  conversation: {
    id: string;
    status: ConversationStatus;
    ticketStatusId?: string | null;
    priority: ConversationPriority;
    assignedAgent?: UpdatedAgent | null;
    assignedTeam?: UpdatedTeam | null;
    assignedById?: string | null;
  },
  change: ConversationChange,
  agents: readonly UpdatedAgent[],
  teams: readonly UpdatedTeam[],
  /** The viewer: who an owner change is predicted to be made by. */
  currentUserId?: string,
): UpdateConversationMutation {
  const reassigned = change.agentId !== undefined;
  const agent = reassigned
    ? (agents.find((a) => a.id === change.agentId) ?? null)
    : (conversation.assignedAgent ?? null);
  const team = reassigned
    ? (teams.find((t) => t.id === change.teamId) ?? null)
    : (conversation.assignedTeam ?? null);
  return {
    conversationUpdate: {
      __typename: "Conversation",
      id: conversation.id,
      status: change.status ?? conversation.status,
      // A category picked alone: the server picks its default status.
      ticketStatusId:
        change.ticketStatusId !== undefined
          ? change.ticketStatusId
          : change.status
            ? null
            : (conversation.ticketStatusId ?? null),
      priority: change.priority ?? conversation.priority,
      assignedAgent: agent && {
        __typename: "User",
        id: agent.id,
        givenName: agent.givenName,
        familyName: agent.familyName,
      },
      assignedTeam: team && {
        __typename: "Team",
        id: team.id,
        name: team.name,
      },
      assignedById: reassigned
        ? (currentUserId ?? null)
        : (conversation.assignedById ?? null),
    },
  };
}
